import React, { useState, useEffect, useRef } from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';
import { Helmet } from 'react-helmet-async';
import { track } from '../utils/analytics';

/* ══════════════════════════════════════════════════════════════════════════
   CONTACT — RevUpAI
   Centrepiece: AI Intake Agent — a conversational interface that qualifies
   visitor intent, demonstrates what Operated AI feels like, and routes to
   the right conversation. The experience IS the product demo.
   ══════════════════════════════════════════════════════════════════════════ */

const C  = '#00D4AA';
const R  = '0,212,170';
const CP = '#7C3AED';
const RP = '124,58,237';
const BG = '#09060F';

/* ── Breakpoint hook ──────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w < 1024 };
}

const Section = ({ children, style = {} }) => (
  <section style={{ padding: `${SECTION_PAD} 0`, position:'relative', ...style }}>
    {children}
  </section>
);
const Cap = ({ children, style = {} }) => (
  <div style={{ ...WRAP, ...style }}>{children}</div>
);
const Eyebrow = ({ children, color = C }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color, margin:'0 0 14px', opacity:.85 }}>{children}</p>
);

/* ══════════════════════════════════════════════════════════════════════════
   HERO
   ══════════════════════════════════════════════════════════════════════════ */
function Hero() {
  return (
    <section style={{ background:BG, padding:`${SECTION_PAD} 0 clamp(48px,5vh,64px)`, position:'relative', overflow:'hidden', textAlign:'center' }}>
      <div aria-hidden="true" style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', top:'-20%', left:'50%', transform:'translateX(-50%)', width:900, height:900, borderRadius:'50%', background:`radial-gradient(circle,rgba(${R},.07) 0%,transparent 65%)`, filter:'blur(120px)' }}/>
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,.012) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.012) 1px,transparent 1px)', backgroundSize:'80px 80px', maskImage:'radial-gradient(ellipse 80% 60% at 50% 40%,black 0%,transparent 100%)' }}/>
      </div>
      <div style={{ ...WRAP, position:'relative', zIndex:2, maxWidth:720 }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 16px 6px 10px', borderRadius:100, marginBottom:28, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)`, animation:'fadeUp .7s ease both .1s' }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:C, animation:'pulse 2s infinite' }}/>
          <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:600, color:`rgba(${R},.85)`, letterSpacing:.3 }}>AI Intake Agent · Online</span>
        </div>
        <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, lineHeight:1.08, letterSpacing:'-2.5px', fontSize:'clamp(36px,4.5vw,64px)', color:'#EDE9F8', margin:'0 0 20px', animation:'fadeUp .8s ease both .2s' }}>
          Start a conversation.<br/><span style={{ color:C }}>Our AI will make sure<br/>it's the right one.</span>
        </h1>
        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,17px)', fontWeight:300, lineHeight:1.8, color:'rgba(255,255,255,.4)', margin:'0 auto', maxWidth:540, animation:'fadeUp .7s ease both .32s' }}>
          Talk to our intake agent below — it'll understand what you're looking for and make sure the right person at RevUpAI reaches out. Your first experience of what Operated AI actually feels like.
        </p>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   AI INTAKE AGENT — conversational qualification engine
   ══════════════════════════════════════════════════════════════════════════ */

// ─── CHANGE 1 OF 4 ────────────────────────────────────────────────────────
// Split the old single 'name' step into two steps: 'name' then 'email'.
// Added email validation so invalid/missing emails block progression.
// The validate function returns null on success or an error string on failure.
// All other steps are identical to the original — do not touch.
// ──────────────────────────────────────────────────────────────────────────
const FLOW = [
  {
    id: 'welcome',
    ai: "Hi — I'm the RevUpAI intake agent. I'll ask you a few questions so we can make sure the right person reaches out to you.\n\nWhat brings you here today?",
    type: 'choice',
    choices: [
      { label: 'I want to deploy a pre-built AI role',     value: 'prebuild',   icon: '⚡' },
      { label: 'I want to build a custom AI agent',        value: 'custom',     icon: '🏗️' },
      { label: 'I want to understand what RevUpAI does',   value: 'explore',    icon: '🔍' },
      { label: 'I have a specific question or request',    value: 'question',   icon: '💬' },
    ],
  },
  {
    id: 'industry',
    ai: (prev) => {
      const map = {
        prebuild: "Great — our pre-built roles can be deployed in days. Which industry are you in? That helps us match you with the right domain expert.",
        custom:   "A custom AI role — that starts with a Discovery engagement. First, which industry are you operating in?",
        explore:  "Happy to help you understand what we do. Which industry are you in? I'll make sure the conversation is relevant to your context.",
        question: "Of course. What industry are you in so I can route your question to the right person?",
      };
      return map[prev.welcome] || "Which industry are you in?";
    },
    type: 'choice',
    choices: [
      { label: 'Banking, Financial Services & Insurance', value: 'bfsi',       icon: '🏦' },
      { label: 'Retail & Commerce',                       value: 'retail',     icon: '🛒' },
      { label: 'Higher Education',                        value: 'highered',   icon: '🎓' },
      { label: 'Healthcare',                              value: 'healthcare', icon: '🏥' },
      { label: 'Technology / SaaS',                       value: 'tech',       icon: '💻' },
      { label: 'Other',                                   value: 'other',      icon: '🏢' },
    ],
  },
  {
    id: 'scale',
    ai: (prev) => {
      const role = prev.welcome === 'prebuild' ? 'the deployment' : 'this engagement';
      return `Understood. How large is the organisation ${role} would be for?`;
    },
    type: 'choice',
    choices: [
      { label: 'Under 500 employees',      value: 'small',      icon: '' },
      { label: '500 – 5,000 employees',    value: 'mid',        icon: '' },
      { label: '5,000 – 50,000 employees', value: 'large',      icon: '' },
      { label: '50,000+ employees',        value: 'enterprise', icon: '' },
    ],
  },
  {
    id: 'timeline',
    ai: () => "What's your timeline for getting started?",
    type: 'choice',
    choices: [
      { label: 'As soon as possible',  value: 'now',      icon: '🔥' },
      { label: 'This quarter',         value: 'quarter',  icon: '📅' },
      { label: 'Next 6 months',        value: 'planning', icon: '🗓️' },
      { label: 'Just exploring',       value: 'research', icon: '🔭' },
    ],
  },
  // Step 1 of 2 — name only (was previously combined with email)
  {
    id: 'name',
    ai: (prev) => {
      const urgency = prev.timeline === 'now' ? " — and given your timeline, we'll prioritise this." : '.';
      return `Perfect${urgency} What's your name?`;
    },
    type: 'input',
    placeholder: 'Your full name — e.g. Alex Chen',
  },
  // Step 2 of 2 — email with validation (new step)
  {
    id: 'email',
    ai: (prev) => {
      const first = prev.name ? prev.name.trim().split(' ')[0] : '';
      return `Thanks${first ? `, ${first}` : ''}. And your work email so we can reach you?`;
    },
    type: 'input',
    placeholder: 'your@company.com',
    validate: (val) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(val.trim())
        ? null
        : "That doesn't look like a valid email — can you double-check it?";
    },
  },
];

function getAIText(step, answers) {
  const txt = step.ai;
  return typeof txt === 'function' ? txt(answers) : txt;
}

// ─── CHANGE 2 OF 4 ────────────────────────────────────────────────────────
// getSummary now reads separate answers.name and answers.email fields
// instead of the old combined answers.name field.
// contact is now split into name + email for cleaner API payload.
// ──────────────────────────────────────────────────────────────────────────
function getSummary(answers) {
  const intentMap = {
    prebuild: 'deploy a pre-built AI role',
    custom:   'build a custom AI role',
    explore:  'learn more about RevUpAI',
    question: 'get a specific question answered',
  };
  const industryMap = {
    bfsi:'Banking & Financial Services', retail:'Retail', highered:'Higher Education',
    healthcare:'Healthcare', tech:'Technology / SaaS', other:'their industry',
  };
  const timelineMap = {
    now:'as soon as possible', quarter:'this quarter',
    planning:'in the next 6 months', research:'when the time is right',
  };
  const scaleMap = {
    small:'under 500 people', mid:'500–5,000 people',
    large:'5,000–50,000 people', enterprise:'50,000+ people',
  };
  return {
    intent:   intentMap[answers.welcome]    || 'get in touch',
    industry: industryMap[answers.industry] || 'their sector',
    scale:    scaleMap[answers.scale]       || 'their organisation',
    timeline: timelineMap[answers.timeline] || 'when ready',
    name:     answers.name  || '',   // separate name field
    email:    answers.email || '',   // separate email field
  };
}

function TypingIndicator() {
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center', padding:'12px 16px' }}>
      {[0,1,2].map(i => (
        <div key={i} style={{ width:6, height:6, borderRadius:'50%', background:`rgba(${R},.6)`, animation:`typingDot 1.2s ease ${i*0.2}s infinite` }}/>
      ))}
    </div>
  );
}

/* ─── Send answers to /api/contact ─────────────────────────────────────── */
async function submitToAPI(answers) {
  const res = await fetch('/api/contact', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ answers }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Submission failed');
  }
  return res.json();
}

function AIIntakeAgent() {
  const { mob } = useBreakpoint();
  const [messages, setMessages]       = useState([]);
  const [stepIdx, setStepIdx]         = useState(0);
  const [answers, setAnswers]         = useState({});
  const [typing, setTyping]           = useState(false);
  const [inputVal, setInputVal]       = useState('');
  const [done, setDone]               = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [inputActive, setInputActive] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // Show first AI message on mount
  useEffect(() => {
    setTimeout(() => {
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        setMessages([{ role:'ai', text: getAIText(FLOW[0], {}), step: FLOW[0] }]);
      }, 1200);
    }, 600);
  }, []);

  useEffect(() => {
    const el = bottomRef.current;
    if (el) {
      const container = el.closest('[data-chat-scroll]');
      if (container) container.scrollTop = container.scrollHeight;
    }
  }, [messages, typing]);

  const advance = (value, label) => {
    const currentStep = FLOW[stepIdx];
    const newAnswers  = { ...answers, [currentStep.id]: value };
    setAnswers(newAnswers);
    setSubmitError(null);

    // Add user message
    setMessages(prev => [...prev, { role:'user', text: label }]);

    const nextIdx = stepIdx + 1;

    if (nextIdx >= FLOW.length) {
      // ── Flow complete — show typing, then send email + show final message ──
      setTimeout(() => setTyping(true), 400);
      setTimeout(async () => {
        // Fire email in background — don't block the UX
        try {
          await submitToAPI(newAnswers);
          track('generate_lead', { method: 'contact_intake' });
        } catch (err) {
          console.error('Contact submission error:', err);
          setSubmitError('We had trouble sending your details automatically. Please email us at hello@revupai.com.');
        }

        setTyping(false);
        const s = getSummary(newAnswers);

        // ─── CHANGE 3 OF 4 ──────────────────────────────────────────────
        // firstName now reads from s.name (was s.contact.split(',')[0]...)
        // This is safe — s.name is always a plain string now.
        // ────────────────────────────────────────────────────────────────
        const firstName = s.name.trim().split(' ')[0] || '';

        const finalMsg  = `Got it${firstName ? `, ${firstName}` : ''} — thank you.\n\nHere's what I've captured:\n\n• Looking to ${s.intent}\n• Industry: ${s.industry}\n• Organisation: ${s.scale}\n• Timeline: ${s.timeline}\n\nI'm routing this to the right person at RevUpAI now. You'll hear from us within one business day — and because we know what you're looking for, the conversation will start in the right place.`;
        setMessages(prev => [...prev, { role:'ai', text: finalMsg, final: true }]);
        setDone(true);
      }, 2000);
      return;
    }

    // Next step
    setTimeout(() => setTyping(true), 400);
    setTimeout(() => {
      setTyping(false);
      setStepIdx(nextIdx);
      const nextStep = FLOW[nextIdx];
      setMessages(prev => [...prev, { role:'ai', text: getAIText(nextStep, newAnswers), step: nextStep }]);
      if (nextStep.type === 'input') {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }, 1400 + Math.random() * 400);
  };

  // ─── CHANGE 4 OF 4 ────────────────────────────────────────────────────
  // submitInput now runs the step's validate function (if present) before
  // calling advance. If validation fails, it shows the error inline and
  // does NOT advance. This is the core fix for the "hii" submission bug.
  // All other behaviour is identical to the original.
  // ──────────────────────────────────────────────────────────────────────
  const submitInput = () => {
    if (!inputVal.trim()) return;

    const currentStep = FLOW[stepIdx];

    // Run validator if this step has one
    if (currentStep.validate) {
      const validationError = currentStep.validate(inputVal.trim());
      if (validationError) {
        setSubmitError(validationError);
        return; // Block progression — don't call advance
      }
    }

    setSubmitError(null);
    advance(inputVal.trim(), inputVal.trim());
    setInputVal('');
  };

  const currentStep = FLOW[stepIdx];

  return (
    <Section style={{ background:BG, paddingTop:'clamp(20px,3vh,40px)' }}>
      <Cap style={{ maxWidth:820 }}>
        <div style={{ border:`1px solid rgba(${R},.2)`, borderRadius:16, overflow:'hidden', background:'rgba(255,255,255,.02)' }}>

          {/* Agent header */}
          <div style={{ padding:'16px 24px', background:`rgba(${R},.06)`, borderBottom:`1px solid rgba(${R},.15)`, display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ position:'relative' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:`rgba(${R},.15)`, border:`1.5px solid rgba(${R},.4)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>✦</div>
              <div style={{ position:'absolute', bottom:1, right:1, width:9, height:9, borderRadius:'50%', background:C, border:`1.5px solid ${BG}`, animation:'pulse 2s infinite' }}/>
            </div>
            <div>
              <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:700, color:'#DDD8F2' }}>RevUpAI Intake Agent</div>
              <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, color:`rgba(${R},.7)`, display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ width:5, height:5, borderRadius:'50%', background:C, display:'inline-block' }}/>
                Online · Typically responds instantly
              </div>
            </div>
            <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
              {['#EF4444','#F59E0B',C].map((c,i) => <div key={i} style={{ width:9, height:9, borderRadius:'50%', background:c, opacity:.5 }}/>)}
            </div>
          </div>

          {/* Message thread */}
          <div data-chat-scroll style={{ padding: mob?'16px':' 24px', minHeight:320, maxHeight: mob?400:480, overflowY:'auto', display:'flex', flexDirection:'column', gap:16 }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', justifyContent: msg.role==='user'?'flex-end':'flex-start', animation:'fadeUp .4s ease both' }}>
                {msg.role === 'ai' && (
                  <div style={{ width:28, height:28, borderRadius:'50%', background:`rgba(${R},.12)`, border:`1px solid rgba(${R},.3)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, flexShrink:0, marginTop:2 }}>✦</div>
                )}
                <div style={{ maxWidth: mob?'88%':'78%' }}>
                  <div style={{ padding:'12px 16px', borderRadius: msg.role==='ai'?'4px 14px 14px 14px':'14px 4px 14px 14px', background: msg.role==='ai'?'rgba(255,255,255,.05)':`rgba(${R},.12)`, border: msg.role==='ai'?'1px solid rgba(255,255,255,.08)':`1px solid rgba(${R},.25)`, fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color: msg.role==='ai'?'rgba(255,255,255,.7)':'rgba(255,255,255,.88)', whiteSpace:'pre-line' }}>
                    {msg.text}
                  </div>

                  {/* Choices */}
                  {msg.role === 'ai' && msg.step?.type === 'choice' && !done && i === messages.length - 1 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:10 }}>
                      {msg.step.choices.map((ch, j) => (
                        <button key={j} onClick={()=>advance(ch.value, ch.label)} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:9, cursor:'pointer', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)', textAlign:'left', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:400, color:'rgba(255,255,255,.62)', transition:'all .18s ease' }}
                          onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.08)`;e.currentTarget.style.borderColor=`rgba(${R},.28)`;e.currentTarget.style.color='rgba(255,255,255,.9)';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.03)';e.currentTarget.style.borderColor='rgba(255,255,255,.08)';e.currentTarget.style.color='rgba(255,255,255,.62)';}}
                        >
                          {ch.icon && <span style={{ fontSize:15, width:20, textAlign:'center' }}>{ch.icon}</span>}
                          {ch.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, flexShrink:0, marginTop:2, color:'rgba(255,255,255,.5)' }}>You</div>
                )}
              </div>
            ))}

            {typing && (
              <div style={{ display:'flex', gap:10, alignItems:'flex-start', animation:'fadeUp .3s ease both' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:`rgba(${R},.12)`, border:`1px solid rgba(${R},.3)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, flexShrink:0 }}>✦</div>
                <div style={{ padding:'4px 8px', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'4px 14px 14px 14px' }}>
                  <TypingIndicator/>
                </div>
              </div>
            )}

            {/* Validation error — shown inline above input when email fails */}
            {submitError && (
              <div style={{ padding:'10px 14px', borderRadius:9, background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, color:'rgba(245,158,11,0.8)', animation:'fadeUp .4s ease both' }}>
                ⚠ {submitError}
              </div>
            )}

            <div ref={bottomRef}/>
          </div>

          {/* Input area */}
          {!done && currentStep?.type === 'input' && messages.length > 0 && !typing && (
            <div style={{ padding:'16px 20px', borderTop:'1px solid rgba(255,255,255,.07)', display:'flex', gap:10 }}>
              <input
                ref={inputRef}
                value={inputVal}
                onChange={e=>{ setInputVal(e.target.value); setSubmitError(null); }}
                onKeyDown={e=>e.key==='Enter'&&submitInput()}
                onFocus={()=>setInputActive(true)}
                onBlur={()=>setInputActive(false)}
                placeholder={currentStep.placeholder}
                style={{ flex:1, padding:'11px 16px', borderRadius:9, background:'rgba(255,255,255,.05)', border:`1px solid ${submitError?'rgba(245,158,11,.5)':inputActive?`rgba(${R},.4)`:'rgba(255,255,255,.1)'}`, fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, color:'#C8C0EC', outline:'none', transition:'border-color .2s', caretColor:C }}
              />
              <button onClick={submitInput} style={{ padding:'11px 20px', borderRadius:9, cursor:'pointer', background:inputVal.trim()?C:'rgba(255,255,255,.06)', color:inputVal.trim()?'#09060F':'rgba(255,255,255,.3)', border:'none', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:700, transition:'all .2s ease' }}>
                Send →
              </button>
            </div>
          )}

          {/* Done footer */}
          {done && (
            <div style={{ padding:'16px 24px', borderTop:`1px solid rgba(${R},.15)`, background:`rgba(${R},.04)`, display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:C, boxShadow:`0 0 10px ${C}` }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, color:`rgba(${R},.75)`, fontWeight:500 }}>Routed to the RevUpAI team · Response within 1 business day</span>
            </div>
          )}
        </div>

        {/* Skip option */}
        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, color:'rgba(255,255,255,.2)', textAlign:'center', marginTop:16 }}>
          Prefer to reach us directly?{' '}
          <a href="mailto:hello@revupai.com" style={{ color:`rgba(${R},.55)`, textDecoration:'none' }}>hello@revupai.com</a>
        </p>
      </Cap>

      <style>{`
        @keyframes typingDot {
          0%,60%,100% { opacity:.3; transform:scale(1); }
          30% { opacity:1; transform:scale(1.3); }
        }
      `}</style>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   WHAT HAPPENS NEXT
   ══════════════════════════════════════════════════════════════════════════ */
function WhatHappensNext() {
  const { mob } = useBreakpoint();
  const steps = [
    { n:'01', icon:'🤖', label:'Agent routes your request', desc:'Our intake agent classifies your intent and packages your context — so the right person at RevUpAI gets the right brief before they pick up the phone.' },
    { n:'02', icon:'👤', label:'Right person reaches out', desc:'Not a generic sales call. The RevUpAI team member who reaches out is matched to your industry and your intent — pre-built deployment, Discovery, or something else.' },
    { n:'03', icon:'🎯', label:'Conversation starts in the right place', desc:"Because we already know your industry, your scale, and what you're looking to do — the first conversation skips the introductory questions and goes straight to what matters." },
  ];

  return (
    <div style={{ background:`rgba(${R},.025)`, borderTop:`1px solid rgba(${R},.08)`, borderBottom:`1px solid rgba(${R},.08)`, padding:'clamp(52px,6vh,72px) clamp(24px,6vw,100px)' }}>
      <Cap>
        <div style={{ textAlign:'center', marginBottom:44 }}>
          <Eyebrow>What Happens Next</Eyebrow>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(22px,2.2vw,34px)', lineHeight:1.15, letterSpacing:'-1px', color:'#EDE9F8', margin:'0 auto', maxWidth:560 }}>
            You'll hear from the right person. Within one business day.
          </h2>
        </div>

        <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'repeat(3,1fr)', gap: mob?12:2 }}>
          {steps.map((s,i) => (
            <div key={i} style={{ padding:'28px 26px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderTop:`3px solid ${i===0?C:i===1?'#8B5CF6':'#10B981'}`, position:'relative' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <span style={{ fontSize:20 }}>{s.icon}</span>
                <span style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:11, fontWeight:700, color:'rgba(255,255,255,.2)', letterSpacing:1 }}>{s.n}</span>
              </div>
              <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(255,255,255,.28)', marginBottom:8 }}>{s.label}</div>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.75, color:'rgba(255,255,255,.42)', margin:0 }}>{s.desc}</p>
              {i < 2 && !mob && (
                <div style={{ position:'absolute', top:'50%', right:-1, transform:'translateY(-50%)', width:20, height:1, background:'rgba(255,255,255,.08)', zIndex:1 }}/>
              )}
            </div>
          ))}
        </div>
      </Cap>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PRESENCE + DIRECT CONTACT
   ══════════════════════════════════════════════════════════════════════════ */
function Presence() {
  const { mob, tab } = useBreakpoint();
  return (
    <Section style={{ background:BG }}>
      <Cap>
        <div style={{ display:'grid', gridTemplateColumns: tab?'1fr':'1fr 1fr', gap: tab?16:2, alignItems:'stretch' }}>

          {/* Where we are */}
          <div style={{ padding: mob?'28px 24px':'40px 36px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)' }}>
            <Eyebrow>Where We Are</Eyebrow>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(20px,1.8vw,26px)', fontWeight:700, color:'#DDD8F2', margin:'0 0 20px', lineHeight:1.3 }}>
              Headquartered in Dallas, TX.<br/>Team across the US and globally.
            </h3>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {[
                { icon:'📍', label:'Headquarters',   detail:'Dallas, Texas, USA' },
                { icon:'🇺🇸', label:'US Team',        detail:'Engineering, delivery, and domain experts across the United States' },
                { icon:'🌐', label:'Global Delivery', detail:'Nearshore and offshore teams supporting 24/7 operations and delivery' },
                { icon:'🏢', label:'Client Presence', detail:'Active engagements across BFSI, Retail, Higher Education, and Enterprise' },
              ].map((item,i) => (
                <div key={i} style={{ display:'flex', gap:14, alignItems:'flex-start', padding:'13px 16px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:9 }}>
                  <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(255,255,255,.25)', marginBottom:3 }}>{item.label}</div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, color:'rgba(255,255,255,.55)' }}>{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Direct contact */}
          <div style={{ padding: mob?'28px 24px':'40px 36px', background:`rgba(${R},.04)`, border:`1px solid rgba(${R},.18)`, display:'flex', flexDirection:'column' }}>
            <Eyebrow>Direct Contact</Eyebrow>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(20px,1.8vw,26px)', fontWeight:700, color:'#DDD8F2', margin:'0 0 20px', lineHeight:1.3 }}>
              Prefer to reach us directly?<br/>We're straightforward to find.
            </h3>

            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:32, flex:1 }}>
              {[
                { icon:'✉️', label:'General Enquiries',  value:'hello@revupai.com',    href:'mailto:hello@revupai.com' },
                { icon:'🔍', label:'Start a Discovery',  value:'discovery@revupai.com', href:'mailto:discovery@revupai.com' },
                { icon:'⚡', label:'Pre-Built AI Roles', value:'deploy@revupai.com',    href:'mailto:deploy@revupai.com' },
              ].map((item,i) => (
                <a key={i} href={item.href} style={{ display:'flex', gap:14, alignItems:'center', padding:'14px 18px', background:'rgba(255,255,255,.03)', border:`1px solid rgba(${R},.12)`, borderRadius:10, textDecoration:'none', transition:'all .2s ease' }}
                  onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.08)`;e.currentTarget.style.borderColor=`rgba(${R},.3)`;e.currentTarget.style.transform='translateY(-1px)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.03)';e.currentTarget.style.borderColor=`rgba(${R},.12)`;e.currentTarget.style.transform='none';}}
                >
                  <span style={{ fontSize:18, flexShrink:0 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.55)`, marginBottom:2 }}>{item.label}</div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:500, color:`rgba(${R},.85)` }}>{item.value}</div>
                  </div>
                  <span style={{ marginLeft:'auto', color:`rgba(${R},.4)`, fontSize:14 }}>→</span>
                </a>
              ))}
            </div>

            <div style={{ padding:'16px 20px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:9 }}>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:300, lineHeight:1.7, color:'rgba(255,255,255,.35)', margin:0 }}>
                We respond to every enquiry within one business day. If your matter is urgent, mention it in your message and we'll prioritise accordingly.
              </p>
            </div>
          </div>

        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ROOT
   ══════════════════════════════════════════════════════════════════════════ */
export default function Contact() {
  return (
    <>
      <Helmet>
        <title>Contact RevUpAI — Book a Discovery Call | Dallas, Texas</title>
        <meta name="description" content="Book a Discovery Call with RevUpAI. We'll map your AI exposure, identify your highest-value agentic AI opportunity, and tell you what's worth building first. Dallas, Texas. sales@revupai.com" />
        <meta name="keywords" content="contact RevUpAI, book AI demo, AI discovery call, enterprise AI consultation, RevUpAI Dallas, agentic AI consultation, secure AI consultation" />
        <link rel="canonical" href="https://revupai.com/contact" />
        <meta property="og:title" content="Contact RevUpAI — Book a Discovery Call" />
        <meta property="og:description" content="30 minutes. We'll map your AI exposure and tell you what's worth building first." />
        <meta property="og:url" content="https://revupai.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact RevUpAI — Book a Discovery Call" />
        <meta name="twitter:description" content="30 minutes. We'll map your AI exposure and tell you what's worth building first." />
        <meta name="twitter:image" content="https://revupai.com/og-image.png" />
      </Helmet>
      <style>{`
        @keyframes fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes pulse   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.8)} }
        * { box-sizing:border-box; }
        ::placeholder { color:rgba(255,255,255,.2); }
      `}</style>
      <div style={{ background:BG, minHeight:'100vh' }}>
        <Hero/>
        <AIIntakeAgent/>
        <WhatHappensNext/>
        <Presence/>
      </div>
    </>
  );
}